<template>
    <div class="change-boxes" :class="{ 'is-light': !$vuetify.theme.dark }">
        <div v-for="(change, i) in changes" :key="i" class="change-group">
            <div class="change-label">{{ labelOf(change) }}</div>

            <div v-if="change.type === 'sets'" class="change-row">
                <div v-for="(set, j) in change.sets" :key="j" class="change-box"
                    :class="{ 'is-changed': hasChanged(set) }">
                    <span class="change-box-tag">{{ set.label }}</span>
                    <span v-if="hasChanged(set)" class="change-box-old">{{ set.from }}</span>
                    <span class="change-box-value">{{ set.to }}</span>
                </div>
            </div>

            <div v-else-if="change.type === 'exercise'" class="change-row">
                <div class="change-box is-before">
                    <span class="change-box-value">{{ change.from }}</span>
                </div>
                <span class="change-arrow">&#8594;</span>
                <div class="change-box is-after">
                    <span class="change-box-value">{{ change.to }}</span>
                </div>
            </div>

            <div v-else-if="change.type === 'skip'" class="change-row">
                <div class="change-box is-before">
                    <span class="change-box-value">{{ change.exercise }}</span>
                </div>
            </div>

            <div v-else-if="change.type === 'pain'" class="change-row">
                <div class="change-box is-changed">
                    <span class="change-box-value">{{ change.value }}/10</span>
                </div>
            </div>

            <div v-else-if="change.type === 'comment'" class="change-quote">{{ change.text }}</div>

            <template v-else-if="change.type === 'video'">
                <span v-if="change.expired" class="change-expired">{{ $t('common.video_unavailable') }}</span>
                <button v-else type="button" class="change-video-btn" @click.stop="$emit('click:video', change)">
                    <v-icon size="11">mdi-play</v-icon>
                    {{ $t('exercises.watch_video') }}
                </button>
            </template>
        </div>
    </div>
</template>

<script>
export default {
    name: "ExerciseChangeBoxes",
    props: {
        changes: {
            type: Array,
            default: () => []
        },
    },
    methods: {
        hasChanged(set) {
            return set?.from != null && set.from !== set.to
        },
        labelOf(change) {
            const vm = this
            if (change?.type === 'sets') return vm.setsLabel(change.name)
            const labels = {
                exercise: vm.$t('common.exercise_changed'),
                comment: vm.$t('common.comment'),
                video: vm.$t('common.recorded_video'),
                skip: vm.$t('common.exercise_skipped'),
                pain: vm.$t('common.pain_scale'),
            }
            return labels[change?.type] || ''
        },
        setsLabel(name) {
            const vm = this
            const key = `exercises.${String(name || '').replace('short_', '')}`
            const label = vm.$te(key) ? vm.$t(key) : name
            return `${label} ${vm.$t('exercises.per_set')}`
        },
    }
}
</script>

<style scoped>
.change-boxes {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.change-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: var(--v-primary-base);
    margin-bottom: 7px;
}

.is-light .change-label {
    color: var(--v-accent-base);
}

.change-row {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.change-box {
    background: rgba(204, 255, 51, .10);
    border: 1px solid rgba(204, 255, 51, .25);
    border-radius: 6px;
    padding: 4px 9px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    min-width: 34px;
}

.is-light .change-box {
    background: rgba(51, 153, 153, .10);
    border-color: rgba(51, 153, 153, .25);
}

.change-box.is-changed,
.change-box.is-after {
    background: rgba(204, 255, 51, .22);
    border-color: var(--v-primary-base);
}

.is-light .change-box.is-changed,
.is-light .change-box.is-after {
    background: rgba(51, 153, 153, .20);
    border-color: var(--v-secondary-base);
}

.change-box.is-before {
    opacity: .6;
}

.change-box.is-before .change-box-value {
    text-decoration: line-through;
}

.change-box-tag {
    font-size: 8px;
    font-weight: 700;
    letter-spacing: .03em;
    text-transform: uppercase;
    color: var(--v-txtSecondary-base);
}

.change-box-old {
    font-size: 10px;
    font-weight: 700;
    color: var(--v-txtSecondary-base);
    text-decoration: line-through;
    text-decoration-thickness: 1.5px;
    white-space: nowrap;
}

.change-box-value {
    font-size: 12px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
}

.change-arrow {
    color: var(--v-primary-base);
    font-weight: 700;
    font-size: 13px;
    flex-shrink: 0;
}

.is-light .change-arrow {
    color: var(--v-secondary-base);
}

.change-quote {
    font-size: 11px;
    font-weight: 500;
    font-style: italic;
    color: var(--v-txtSecondary-base);
    background: rgba(204, 255, 51, .08);
    border: 1px solid rgba(204, 255, 51, .20);
    border-radius: 6px;
    padding: 7px 10px;
    max-width: 220px;
    white-space: normal;
}

.is-light .change-quote {
    background: rgba(51, 153, 153, .08);
    border-color: rgba(51, 153, 153, .20);
}

.change-expired {
    font-size: 11px;
    font-weight: 500;
    color: var(--v-txtMuted-base);
}

.change-video-btn {
    pointer-events: auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: var(--v-primary-base);
    color: #0C0E12;
    border: none;
    border-radius: 20px;
    padding: 6px 12px 6px 10px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
}

.change-video-btn .v-icon {
    color: #0C0E12;
}

.change-video-btn:hover {
    filter: brightness(1.08);
}
</style>
